import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, withApollo } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import Alert from '../../../../components/global/Alert';
import FormGroup from '../../../../components/global/FormGroup';
import Button from '../../../../components/global/Button';
import {
  MeQuery,
  accountUsernameExists,
} from '../../../../graphql/queries/account';
import { accountUpdate as accountUpdateMutation } from '../../../../graphql/mutations/account';
import { getErrorString } from '../../../../util/normalize-errors';

const propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  actionProps: PropTypes.shape({
    onUsernameSelected: PropTypes.func,
  }),
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  accountUpdate: PropTypes.func,
  className: PropTypes.string,
  client: PropTypes.shape({
    query: PropTypes.func,
  }),
};

const UsernameSelect = ({
  onRequestClose,
  actionProps: { onUsernameSelected },
  className,
  accountUpdate,
  user,
  client,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');

  const onSubmitUsername = async event => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      // check username exists, display error and break or continue
      const {
        data: {
          accountUsernameExists: { exists },
        },
      } = await client.query({
        query: accountUsernameExists,
        variables: {
          username,
        },
      });

      if (exists) {
        setError(`${username} is already taken, please try a different one.`);
        setLoading(false);
        return;
      }

      await accountUpdate({
        id: user.id,
        username,
      });

      onUsernameSelected?.();
      onRequestClose();
    } catch (err) {
      setError(getErrorString(err));
      setLoading(false);
    }
  };

  if (!user) {
    return null; // Do not render if no user is logged in.
  }

  return (
    <div className={className}>
      {error && <Alert type="danger">{error}</Alert>}
      <form onSubmit={onSubmitUsername} method="POST">
        <p>
          Uh oh! It looks like you still don&rsquo;t have a username in our
          system! No problem though, let&rsquo;s add one now:
        </p>

        <FormGroup
          id="username"
          type="text"
          label="Username"
          name="username"
          onChange={event => {
            setUsername(event.target.value);
          }}
          required
        />

        <Button type="submit" disabled={loading || !username}>
          {loading ? 'Loading...' : 'Submit'}
        </Button>
      </form>
    </div>
  );
};

UsernameSelect.propTypes = propTypes;

export { UsernameSelect as UsernameSelectComponent };

const withAccountUpdateMutation = graphql(accountUpdateMutation, {
  props: ({ mutate }) => ({
    accountUpdate: account =>
      mutate({
        variables: {
          input: account,
        },
      }),
  }),
});

const withUserData = graphql(MeQuery, {
  props: ({ data }) => ({
    user: data?.me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

export default compose(
  withApollo,
  connect(mapStateToProps),
  withUserData,
  withAccountUpdateMutation,
)(UsernameSelect);
